import React, { useState, useEffect, useRef } from 'react';
import RedrawDataToPost from 'components/layout/RedrawDataToPost.jsx';

// Ranks the combinations of a section's nests (LayoutActions#layout_part_nesting_combinations) on the sheet the
// member it is given is drawn on, and hands them to the section once, which selects the best. The server ranks them on
// a job and answers 'processing' until it has, so it asks again; a redraw on another sheet before then asks afresh,
// and the older ranking is never handed over. Nothing shows meanwhile: the section stays selected and usable.
export const POLL_INTERVAL_MILLISECONDS = 1500;
// The server frees a ranking nobody finished after five minutes (LayoutPartNestingCombinationsAnswer::PROCESSING_TTL).
export const POLL_DEADLINE_MILLISECONDS = 5 * 60 * 1000;

const LayoutNestCombinations = ({ combinationsUrl, member, busy, onRanked }) => {
  const [ranked, setRanked] = useState(null);
  const askNumber = useRef(0);
  const handedOver = useRef(false);

  const ask = (body, number, startedAt) => {
    fetch(combinationsUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then((response) => response.json())
      .then((data) => {
        if (number !== askNumber.current) { return; }

        if (data.status === 'processing') {
          if (Date.now() - startedAt < POLL_DEADLINE_MILLISECONDS) {
            setTimeout(() => ask(body, number, startedAt), POLL_INTERVAL_MILLISECONDS);
          }
          return;
        }

        // A viewer whose redraws are denied would only be shown the drawing defaults again, once per nest.
        if (data.status === 'completed' && data.result === 'OK') { setRanked({ combinations: data.combinations, layout: body.layout }); }
      })
      // A ranking that cannot be asked for leaves the section as it is.
      .catch(() => {});
  };

  useEffect(() => {
    const listener = (event) => {
      if (handedOver.current || event.detail !== member.uuid) { return; }

      askNumber.current += 1;
      setRanked(null);
      ask(RedrawDataToPost({ uuid: member.uuid, member: member }), askNumber.current, Date.now());
    };
    document.addEventListener('layout_redraw_finished', listener);
    return () => { document.removeEventListener('layout_redraw_finished', listener); };
  }, []);

  // Never while the section is being redrawn: its tabs would be replaced mid-redraw.
  useEffect(() => {
    if (ranked == null || busy || handedOver.current) { return; }

    handedOver.current = true;
    onRanked(ranked.combinations, ranked.layout);
    setRanked(null);
  }, [ranked, busy]);

  return null;
};

export default LayoutNestCombinations;
